import { useState, useEffect, useRef } from "react";
import { useGetICalEventsQuery } from "app/services/api/calendarApi";
import { AppointmentTypeEnums } from "app/services/api/types/calendar/enums/AppointmentTypeEnums";
import {
  AppointmentStyle,
  APPOINTMENT_STYLES,
} from "app/constants/calendarAppointmentStyleConstants";
import ICAL from "ical.js";
import { EventInput, ViewApi } from "@fullcalendar/core";
import { format } from "date-fns";
import FullCalendar from "@fullcalendar/react";
import { formatAttendingResidents } from "ui/utils/nameUtil";

const compareIcalDates = (a: ICAL.Time, b?: ICAL.Time) =>
  a.day === b?.day && a.month === b?.month && a.year === b?.year;

interface Props {
  residentId?: number;
}
export function useHandleCalendar({ residentId }: Props) {
  const [currentView, setCurrentView] = useState(() => {
    return localStorage.getItem("calendarView") || "timeGridDay";
  });
  const [currentTitle, setCurrentTitle] = useState("");
  const calendarRef = useRef<FullCalendar>(null);

  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: "",
    endDate: "",
  });

  const { data: icalData } = useGetICalEventsQuery(
    {
      filter: {
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        resident_id: residentId,
      },
    },
    { skip: !dateRange.startDate || !dateRange.endDate },
  );

  const handleDatesSet = ({ view }: { view: ViewApi }) => {
    const startDate = view.activeStart;
    const endDate = view.activeEnd;
    if (!startDate || !endDate) return;
    setDateRange({
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
    });
    setCurrentTitle(view.title);
  };

  const [events, setEvents] = useState<EventInput[]>([]);

  const getAppointmentStyleByType = (
    type: AppointmentTypeEnums | string,
  ): AppointmentStyle => {
    return (
      APPOINTMENT_STYLES[type as AppointmentTypeEnums] ??
      APPOINTMENT_STYLES[AppointmentTypeEnums.OTHER]
    );
  };

  const switchView = (view: string) => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(view);
      setCurrentView(view);
      localStorage.setItem("calendarView", view);
      setCurrentTitle(calendarApi.view.title);
    }
  };

  useEffect(() => {
    if (icalData && typeof icalData === "string") {
      try {
        const jcalData = ICAL.parse(icalData);
        const comp = new ICAL.Component(jcalData);
        const vevents = comp.getAllSubcomponents("vevent");
        const endDateLimit = new Date(dateRange.endDate);

        const parsedEvents: EventInput[] = vevents.flatMap((vevent, index) => {
          try {
            const event = new ICAL.Event(vevent);
            const type =
              (vevent.getFirstPropertyValue("type") as string) || "OTHER";
            const id = vevent.getFirstPropertyValue("id") as string;

            if (!event.startDate || !event.endDate) {
              return [];
            }

            const colors = getAppointmentStyleByType(type);

            const recurrenceProperties = vevent.getAllProperties(
              "recurrence-id",
            ) as ICAL.Property[] | undefined;

            const excludedDates = vevent.getAllProperties("exdate") as
              | ICAL.Property[]
              | undefined;

            const checkIsCompleted = (date: ICAL.Time) => {
              return !!recurrenceProperties?.some((property) =>
                compareIcalDates(date, property.getFirstValue() as ICAL.Time),
              );
            };
            const checkIsExcluded = (date: ICAL.Time) => {
              return !!excludedDates?.some((property) =>
                compareIcalDates(date, property.getFirstValue() as ICAL.Time),
              );
            };
            const residents = event.attendees?.map((attendee) => {
              const jCalAttendee = attendee.jCal;
              return jCalAttendee[1].cn || "No resident";
            });
            const formattedResidents = formatAttendingResidents(
              residents,
              undefined,
              true,
            );
            // Handle recurrence
            if (event.isRecurring()) {
              const recurrenceExpander = new ICAL.RecurExpansion({
                component: vevent,
                dtstart: event.startDate,
              });

              console.log("exdates", excludedDates);

              const expandedEvents: EventInput[] = [];

              const duration =
                event.endDate.toJSDate().getTime() -
                event.startDate.toJSDate().getTime();

              let next: ICAL.Time;
              while ((next = recurrenceExpander.next())) {
                // Stop if the occurrence is beyond the end date
                if (next.toJSDate() > endDateLimit) {
                  break;
                }

                if (!checkIsExcluded(next)) {
                  const isCompleted = checkIsCompleted(next);
                  expandedEvents.push({
                    title: `${event.summary} - ${formattedResidents}`,
                    start: next.toJSDate(),
                    end: new Date(next.toJSDate().getTime() + duration),
                    description: event.description,
                    location: event.location,
                    color: colors.borderLeftColor,
                    textColor: isCompleted ? "#ffffff" : colors.textColor,
                    backgroundColor: isCompleted
                      ? colors.borderLeftColor
                      : colors.backgroundColor,
                    allDay: event.startDate.isDate,
                    extendedProps: {
                      type,
                      id,
                      isCompleted,
                    },
                    residents,
                  });
                }
              }
              return expandedEvents;
            }

            if (checkIsExcluded(event.startDate)) return [];

            const isCompleted = checkIsCompleted(event.startDate);
            // Non-recurring event
            return [
              {
                title: `${event.summary} - ${formattedResidents}`,
                start: event.startDate.toJSDate(),
                end: event.endDate.toJSDate(),
                description: event.description,
                location: event.location,
                color: colors.borderLeftColor,
                textColor: isCompleted ? "#ffffff" : colors.textColor,
                backgroundColor: isCompleted
                  ? colors.borderLeftColor
                  : colors.backgroundColor,
                allDay: event.startDate.isDate,
                extendedProps: {
                  type,
                  id,
                  isCompleted,
                },
                residents,
              },
            ];
          } catch (err) {
            console.warn(`❌ Failed to parse event ${index + 1}:`, err);
            return [];
          }
        });

        setEvents(parsedEvents);
      } catch (err) {
        console.error("iCal parse error:", err);
        setEvents([]);
      }
    }
  }, [icalData]);

  return {
    calendarRef,
    currentView,
    currentTitle,
    switchView,
    data: events,
    // isError,
    // error,
    handleDatesSet,
  };
}
